import { WorklistViewModel } from 'models';

import { useMobileDetection } from 'core/hooks/use-mobile-detection';

/**
 * Determines if the worklist sidebar should be collapsed by default
 * Based on the requirements:
 * - Mobile: Always collapsed by default
 * - Desktop with 0 or 1 worklist: Collapsed by default
 * - Desktop with >1 worklist: Expanded by default
 */
export function shouldWorklistSidebarBeCollapsed(isMobile: boolean, worklists: WorklistViewModel[] | null, isInitialLoad = false): boolean {
  // Always collapse on mobile
  if (isMobile) {
    return true;
  }

  // If no worklists or loading, default to collapsed
  if (!worklists || worklists.length === 0) {
    return true;
  }

  // Count top-level worklists (those without a parent)
  const topLevelWorklists = worklists.filter((w) => w.parentViewId == null);

  // Desktop with 0 or 1 top-level worklist: collapsed
  // Desktop with >1 top-level worklist: expanded
  return topLevelWorklists.length <= 1;
}

/**
 * Determines the worklist sidebar state with reasoning
 * Returns both the decision and the reason for debugging/logging
 */
export function determineWorklistSidebarState(
  isMobile: boolean,
  worklists: WorklistViewModel[] | null,
  isInitialLoad = false,
): {
  shouldExpand: boolean;
  reason: string;
} {
  const shouldCollapse = shouldWorklistSidebarBeCollapsed(isMobile, worklists, isInitialLoad);

  let reason: string;
  if (isMobile) {
    reason = 'Mobile device detected - defaulting to collapsed';
  } else if (!worklists || worklists.length === 0) {
    reason = 'No worklists available - defaulting to collapsed';
  } else {
    const topLevelCount = worklists.filter((w) => w.parentViewId == null).length;
    reason = `Desktop with ${topLevelCount} top-level worklist${topLevelCount === 1 ? '' : 's'} - defaulting to ${shouldCollapse ? 'collapsed' : 'expanded'}`;
  }

  return {
    shouldExpand: !shouldCollapse,
    reason,
  };
}

/**
 * Hook that combines mobile detection with worklist sidebar logic
 * This is the main hook that worklist components should use
 */
export function useWorklistSidebarState(
  worklists: WorklistViewModel[] | null,
  isInitialLoad = false,
): {
  isMobile: boolean;
  shouldExpand: boolean;
  reason: string;
} {
  const { isMobile } = useMobileDetection();
  const { shouldExpand, reason } = determineWorklistSidebarState(isMobile, worklists, isInitialLoad);

  return {
    isMobile,
    shouldExpand,
    reason,
  };
}
